import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useStore } from '../store/useStore';
import {
  getFormSubmission, updateFormSubmission,
  uploadFormAttachment, deleteFormAttachment, getFormAttachmentUrl,
  getConcreteMixes, getUsedInOptions,
} from '../utils/api';
import WeatherCard from './WeatherCard';
import AttachmentLightbox from './AttachmentLightbox';
import UsedInMultiSelect from './UsedInMultiSelect';
import styles from './DailyReportForm.module.css';

// Daily Report form. Handles both editing (`readOnly={false}`) and PM
// detail view (`readOnly={true}`). Auto-loads the submission by id.
//
// Sections (matches the printed-PDF structure in the user's reference):
//   1.1 Description of Work   (multiline freeform text)
//   1.2 Concrete Log          (repeating rows: qty CY / mix id / level / member type)
//   1.3 Extra Work            (yes/no/n/a + optional note)
//   References & Attachments  (photos / videos / documents — form-scoped)
//
// Save Draft persists the data fields without flipping status; Submit
// flips status='submitted'. Once submitted, only admin/pm can edit
// (server-enforced; UI just hides the edit affordances for everyone else).

export default function DailyReportForm({ submissionId, readOnly, onClose }) {
  const { role, currentUser, project } = useStore();
  const isWriter = role === 'admin' || role === 'pm';

  const [sub, setSub]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Local copy of the editable data so we can debounce / batch saves.
  const [data, setData]       = useState({});
  const dirtyRef              = useRef(false);

  // ── Load submission ──
  async function refresh() {
    setLoading(true);
    try {
      const s = await getFormSubmission(submissionId);
      setSub(s);
      setData(s.data || {});
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { if (submissionId) refresh(); /* eslint-disable-next-line */ }, [submissionId]);

  // Effective edit lock: parent says read-only, OR submitted+not writer.
  const submitted   = sub?.status === 'submitted';
  const canEdit     = !readOnly && (!submitted || isWriter);

  function patch(partial) {
    setData((d) => ({ ...d, ...partial }));
    dirtyRef.current = true;
  }

  async function handleSaveDraft() {
    if (!canEdit) return;
    setSaving(true);
    try {
      const fresh = await updateFormSubmission(sub.id, { data });
      setSub(fresh);
      dirtyRef.current = false;
      toast.success('Draft saved');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  }

  async function handleSubmit() {
    if (!canEdit) return;
    if (!window.confirm('Submit this Daily Report? Once submitted, only PM/Admin can edit.')) return;
    setSubmitting(true);
    try {
      const fresh = await updateFormSubmission(sub.id, { data, status: 'submitted' });
      setSub(fresh);
      dirtyRef.current = false;
      toast.success('Submitted');
      onClose?.();
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  }

  // PM "unlock" — flip submitted back to draft. Server permits this for
  // admin/pm only. Skips the warning for the second click.
  async function handleUnsubmit() {
    if (!isWriter || !submitted) return;
    if (!window.confirm('Unlock this submitted report so it can be edited again?')) return;
    setSubmitting(true);
    try {
      const fresh = await updateFormSubmission(sub.id, { status: 'draft' });
      setSub(fresh);
      toast.success('Unlocked — back to draft');
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  }

  // Warn before navigating away with unsaved changes.
  useEffect(() => {
    function onBefore(e) {
      if (dirtyRef.current && canEdit) {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, [canEdit]);

  if (loading || !sub) {
    return (
      <div className={styles.page}>
        <div className={styles.empty}>Loading…</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header bar — close + status + save / submit / unlock buttons. */}
      <header className={styles.header}>
        <button className={styles.backBtn} onClick={onClose}>
          <BackIcon /> Back to forms
        </button>
        <div className={styles.headerCenter}>
          <span className={styles.headerTitle}>Daily Report</span>
          <span className={`${styles.statusPill} ${submitted ? styles.statusSubmitted : styles.statusDraft}`}>
            {sub.status.toUpperCase()}
          </span>
        </div>
        <div className={styles.headerActions}>
          {canEdit && (
            <>
              <button className={styles.btnSecondary} onClick={handleSaveDraft} disabled={saving || submitting}>
                {saving ? 'Saving…' : 'Save Draft'}
              </button>
              <button className={styles.btnPrimary} onClick={handleSubmit} disabled={saving || submitting}>
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </>
          )}
          {/* PM/Admin viewing a submitted report can flip it back to draft to allow edits */}
          {submitted && isWriter && readOnly && (
            <button className={styles.btnSecondary} onClick={handleUnsubmit} disabled={submitting}>
              {submitting ? 'Unlocking…' : 'Unlock'}
            </button>
          )}
        </div>
      </header>

      {/* Metadata strip — date, submitter, last update, etc. */}
      <div className={styles.metaStrip}>
        <MetaItem label="Form date">
          {canEdit ? (
            <input
              type="date"
              className={styles.inputDate}
              value={data.form_date || ''}
              onChange={(e) => patch({ form_date: e.target.value })}
            />
          ) : (
            <span>{data.form_date ? format(new Date(data.form_date + 'T00:00:00'), 'MMM d, yyyy') : '—'}</span>
          )}
        </MetaItem>
        <MetaItem label="Submitted by">
          <span>{sub.submitted_by || '—'}</span>
        </MetaItem>
        <MetaItem label="Created by">
          <span>{sub.created_by || '—'}</span>
        </MetaItem>
        <MetaItem label="Last update">
          <span>{sub.updated_at ? format(new Date(sub.updated_at), 'MMM d, yyyy h:mm a') : '—'}</span>
        </MetaItem>
      </div>

      <div className={styles.body}>
        {/* Weather snapshot — auto-fetches for the form date, caches into
            submission.data.weather so submitted reports stay frozen. We
            update `data` *silently* (no dirty flag) — auto-fetched weather
            isn't a user edit, but it does ride along on the next Save Draft
            or Submit so the snapshot gets persisted with the rest of the form. */}
        <WeatherCard
          projectId={project?.id}
          date={data.form_date}
          cached={data.weather}
          readOnly={!canEdit}
          onSnapshot={(w) => setData((d) => ({ ...d, weather: w }))}
        />

        {/* 1.1 Description of Work */}
        <Section number="1.1" title="Description of Work" hint="What did you do today?">
          {canEdit ? (
            <textarea
              className={styles.textarea}
              rows={10}
              placeholder="Mobilization, work performed, plan for tomorrow, etc."
              value={data.description_of_work || ''}
              onChange={(e) => patch({ description_of_work: e.target.value })}
            />
          ) : (
            <pre className={styles.readonlyText}>{data.description_of_work || '—'}</pre>
          )}
        </Section>

        {/* 1.2 Concrete Log */}
        <Section
          number="1.2"
          title="Concrete Log"
          hint='Each placement: Quantity / Mix ID / Level or Area / Member Type. e.g. "100 CY / #50008 / Level 3 / Elevated Slab".'
        >
          <ConcreteLog
            rows={data.concrete_log || []}
            canEdit={canEdit}
            onChange={(rows) => patch({ concrete_log: rows })}
          />
        </Section>

        {/* 1.3 Extra Work */}
        <Section
          number="1.3"
          title="Extra Work"
          hint="Was extra work performed today? If yes, describe and submit a signed extra-work ticket to the PM."
        >
          <ExtraWork
            value={data.extra_work || 'na'}
            note={data.extra_work_note || ''}
            canEdit={canEdit}
            onChange={(extra_work, extra_work_note) => patch({ extra_work, extra_work_note })}
          />
        </Section>

        {/* References & Attachments */}
        <Section title="References &amp; Attachments">
          <Attachments
            submissionId={sub.id}
            attachments={sub.attachments || []}
            canEdit={canEdit}
            onChanged={refresh}
          />
        </Section>
      </div>
    </div>
  );
}

function MetaItem({ label, children }) {
  return (
    <div className={styles.metaItem}>
      <span className={styles.metaLabel}>{label}</span>
      <span className={styles.metaValue}>{children}</span>
    </div>
  );
}

function Section({ number, title, hint, children }) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        {number && <span className={styles.sectionNumber}>{number}</span>}
        <h3 className={styles.sectionTitle}>{title}</h3>
      </div>
      {hint && <p className={styles.sectionHint}>{hint}</p>}
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}

// ── Concrete Log: repeating cards ──
// Shape per row (new):
//   { quantity_cy, mix_id, mix_id_custom, level_or_area, used_in: string[] }
// Backwards compat: legacy rows with `member_type` are normalised on render
// into a single-element used_in array. On save we drop member_type so the
// row matches the new schema once the user touches it.
function ConcreteLog({ rows, canEdit, onChange }) {
  const { project } = useStore();
  const [mixes, setMixes]               = useState([]);
  const [usedInOptions, setUsedInOptions] = useState([]);

  useEffect(() => {
    if (!project) return;
    let alive = true;
    Promise.all([
      getConcreteMixes(project.id).catch(() => []),
      getUsedInOptions(project.id).catch(() => []),
    ]).then(([m, u]) => {
      if (!alive) return;
      setMixes(m); setUsedInOptions(u);
    });
    return () => { alive = false; };
  }, [project?.id]);

  function update(i, patch) {
    onChange(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  }
  function add() {
    onChange([
      ...rows,
      { quantity_cy: '', mix_id: '', mix_id_custom: '', level_or_area: '', used_in: [] },
    ]);
  }
  function remove(i) {
    onChange(rows.filter((_, idx) => idx !== i));
  }

  if (rows.length === 0 && !canEdit) {
    return <div className={styles.muted}>NA</div>;
  }

  return (
    <div className={styles.concreteLog}>
      {rows.map((row, i) => (
        <ConcreteRow
          key={i}
          row={row}
          mixes={mixes}
          usedInOptions={usedInOptions}
          canEdit={canEdit}
          onPatch={(patch) => update(i, patch)}
          onRemove={() => remove(i)}
        />
      ))}
      {canEdit && (
        <button className={styles.addRowBtn} onClick={add}>
          <PlusIcon /> Add placement
        </button>
      )}
      {rows.length === 0 && canEdit && (
        <div className={styles.mutedSmall}>No placements yet — click "Add placement" if any concrete was poured today.</div>
      )}
    </div>
  );
}

// One placement card. Layout:
//   [ Qty (CY) ]  [ Mix ID select ]  [ custom mix input — when "Different" picked ]
//   [ Level / Area / Pour ]
//   [ Used In multi-select with custom add ]
//   [ Remove ]
function ConcreteRow({ row, mixes, usedInOptions, canEdit, onPatch, onRemove }) {
  // Normalise legacy member_type → used_in (single-element array) for
  // display; the next save through onPatch persists the new shape.
  const usedIn = useMemo(() => {
    if (Array.isArray(row.used_in))    return row.used_in;
    if (typeof row.used_in === 'string' && row.used_in) return [row.used_in];
    if (row.member_type)               return [row.member_type];
    return [];
  }, [row.used_in, row.member_type]);

  // The select knows "is this mix in the catalog or custom?". When the
  // user picks "Different Mix...", we keep the existing mix_id in
  // mix_id_custom and clear it from mix_id; the text field then surfaces.
  const inCatalog = mixes.some((m) => m.mix_id === row.mix_id) && row.mix_id;
  const showsCustom = !!row.mix_id_custom || (!inCatalog && !!row.mix_id) || (row._customMode === true);

  function handleMixSelect(e) {
    const v = e.target.value;
    if (v === '__custom__') {
      onPatch({ _customMode: true, mix_id: row.mix_id_custom || '' });
    } else {
      onPatch({ mix_id: v, mix_id_custom: '', _customMode: false });
    }
  }

  if (!canEdit) {
    return (
      <div className={styles.concreteCard}>
        <div className={styles.concreteCardRow}>
          <Cell label="Qty (CY)">{row.quantity_cy || '—'}</Cell>
          <Cell label="Mix ID">{row.mix_id || '—'}</Cell>
          <Cell label="Level / Area / Pour">{row.level_or_area || '—'}</Cell>
          <Cell label="Used In">
            {usedIn.length
              ? usedIn.map((u) => <span key={u} className={styles.usedInTagRO}>{u}</span>)
              : '—'}
          </Cell>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.concreteCard}>
      {/* All four placement fields + trash sit on a single row. The
          Used In chip area can still wrap internally (selected chips
          + suggestion chips), but the outer row stays one line so
          rows don't take 4 lines of vertical space each. */}
      <div className={styles.concreteCardRow}>
        <Cell label="Qty (CY)">
          <input
            className={styles.input} type="number" step="0.01" inputMode="decimal"
            value={row.quantity_cy || ''}
            onChange={(e) => onPatch({ quantity_cy: e.target.value })}
          />
        </Cell>
        <Cell label="Mix ID">
          <select
            className={styles.input}
            value={showsCustom ? '__custom__' : (row.mix_id || '')}
            onChange={handleMixSelect}
          >
            <option value="">— select —</option>
            {mixes.map((m) => {
              const usedInLabel = Array.isArray(m.used_in)
                ? m.used_in.join(', ')
                : (m.used_in || '');
              return (
                <option key={m.id} value={m.mix_id}>
                  {m.mix_id}{m.strength_psi ? ` · ${m.strength_psi} PSI` : ''}{usedInLabel ? ` · ${usedInLabel}` : ''}
                </option>
              );
            })}
            <option value="__custom__">Different Mix…</option>
          </select>
          {showsCustom && (
            <input
              className={styles.input}
              placeholder="Type custom mix ID"
              value={row.mix_id || ''}
              onChange={(e) => onPatch({ mix_id: e.target.value, mix_id_custom: e.target.value })}
              style={{ marginTop: 4 }}
            />
          )}
        </Cell>
        <Cell label="Level / Area / Pour">
          <input
            className={styles.input}
            placeholder='e.g. "Level 3 / North Pour"'
            value={row.level_or_area || ''}
            onChange={(e) => onPatch({ level_or_area: e.target.value })}
          />
        </Cell>
        <Cell label="Used In">
          <UsedInMultiSelect
            options={usedInOptions.map((o) => o.name)}
            value={usedIn}
            onChange={(arr) => onPatch({ used_in: arr, member_type: undefined })}
            allowCustom
          />
        </Cell>
        <button className={styles.removeRowBtn} title="Remove placement" onClick={onRemove}>
          <TrashIcon />
        </button>
      </div>
    </div>
  );
}

function Cell({ label, full, children }) {
  return (
    <div className={`${styles.concreteCell} ${full ? styles.concreteCellFull : ''}`}>
      <span className={styles.concreteCellLabel}>{label}</span>
      <div className={styles.concreteCellValue}>{children}</div>
    </div>
  );
}

// ── Extra Work: radio yes/no/na + note when yes ──
function ExtraWork({ value, note, canEdit, onChange }) {
  return (
    <div className={styles.extraWork}>
      <div className={styles.radioRow}>
        {['yes','no','na'].map((v) => (
          <label key={v} className={styles.radioLabel}>
            <input
              type="radio"
              name="extra_work"
              value={v}
              checked={value === v}
              disabled={!canEdit}
              onChange={() => onChange(v, note)}
            />
            <span>{v === 'na' ? 'N/A' : v.charAt(0).toUpperCase() + v.slice(1)}</span>
          </label>
        ))}
      </div>
      {value === 'yes' && (
        canEdit ? (
          <textarea
            className={styles.textarea}
            rows={3}
            placeholder="Briefly describe the extra work (a signed extra-work ticket should also go to the PM)."
            value={note}
            onChange={(e) => onChange(value, e.target.value)}
          />
        ) : (
          <pre className={styles.readonlyText}>{note || '—'}</pre>
        )
      )}
    </div>
  );
}

// ── Attachments: photo / video / document uploader ──
function Attachments({ submissionId, attachments, canEdit, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(0);
  // ID of the attachment currently being viewed in the lightbox; null = closed.
  const [lightboxId, setLightboxId] = useState(null);
  const inputRef = useRef(null);

  async function handleFiles(files) {
    if (!files?.length) return;
    setUploading(true);
    try {
      // Upload sequentially so progress is meaningful per-file. For multi-file
      // selection we just sum total size for the bar.
      for (const file of files) {
        await uploadFormAttachment({
          submission_id: submissionId,
          file,
          onProgress: (ev) => {
            if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
          },
        });
      }
      toast.success(`Uploaded ${files.length} file${files.length === 1 ? '' : 's'}`);
      onChanged?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function handleDelete(att) {
    if (!window.confirm(`Delete "${att.name}"?`)) return;
    try {
      await deleteFormAttachment(submissionId, att.id);
      onChanged?.();
    } catch (err) { toast.error(err.message); }
  }

  return (
    <div className={styles.attachments}>
      {canEdit && (
        <div
          className={`${styles.dropzone} ${uploading ? styles.dropzoneDisabled : ''}`}
          onClick={() => !uploading && inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            // accept any: photos, videos, docs
            style={{ display: 'none' }}
            onChange={(e) => handleFiles(Array.from(e.target.files || []))}
          />
          {uploading ? (
            <div className={styles.dropzoneProgress}>
              <div className={styles.progressTrack}>
                <div className={styles.progressFill} style={{ width: `${progress}%` }} />
              </div>
              <span className={styles.progressText}>Uploading… {progress}%</span>
            </div>
          ) : (
            <span className={styles.dropzonePrompt}>
              <UploadIcon /> Tap to add photos, videos, or documents
            </span>
          )}
        </div>
      )}

      {attachments.length === 0 && !canEdit && (
        <div className={styles.muted}>No attachments.</div>
      )}

      <div className={styles.attachGrid}>
        {attachments.map((a) => (
          <AttachmentTile
            key={a.id}
            submissionId={submissionId}
            att={a}
            canEdit={canEdit}
            onOpen={() => setLightboxId(a.id)}
            onDelete={() => handleDelete(a)}
          />
        ))}
      </div>

      {/* Lightbox: opens when a tile is clicked. Filtered to images + videos
          internally; clicking a doc tile shows it solo with an "Open in
          new tab" fallback (browsers can't render arbitrary doc types
          inline reliably). Esc / arrow keys + on-screen buttons navigate. */}
      {lightboxId && (
        <AttachmentLightbox
          submissionId={submissionId}
          attachments={attachments}
          activeId={lightboxId}
          onClose={() => setLightboxId(null)}
        />
      )}
    </div>
  );
}

function AttachmentTile({ submissionId, att, canEdit, onOpen, onDelete }) {
  const isImage = att.mime_type?.startsWith('image/');
  const isVideo = att.mime_type?.startsWith('video/');
  const previewUrl  = getFormAttachmentUrl(submissionId, att.id);
  const downloadUrl = getFormAttachmentUrl(submissionId, att.id, { download: true });

  return (
    <div className={styles.attachTile}>
      {/* Tile body opens the lightbox. Images and videos preview inline;
          docs route to the lightbox too — its built-in fallback shows a
          big icon + an "Open in new tab" link rather than a broken viewer. */}
      <button
        type="button"
        className={styles.attachThumbWrap}
        onClick={onOpen}
        aria-label={`Open ${att.name}`}
      >
        {isImage ? (
          <img src={previewUrl} alt={att.name} className={styles.attachThumb} />
        ) : isVideo ? (
          <video src={previewUrl} className={styles.attachThumb} muted preload="metadata" />
        ) : (
          <div className={styles.attachIcon}><FileIcon /></div>
        )}
      </button>
      <div className={styles.attachMeta}>
        <span className={styles.attachName} title={att.name}>{att.name}</span>
        <span className={styles.attachSubmeta}>
          {att.uploaded_by || '—'} · {att.created_at ? format(new Date(att.created_at), 'MMM d, h:mm a') : ''}
        </span>
      </div>
      <div className={styles.attachActions}>
        <a href={downloadUrl} className={styles.iconBtn} title="Download" download={att.name}>
          <DownloadIcon />
        </a>
        {canEdit && (
          <button className={styles.iconBtn} title="Delete" onClick={onDelete}>
            <TrashIcon />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Icons ──
const BackIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M7.5 2L3.5 6L7.5 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const PlusIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);
const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M3 4h10M6 4V3h4v1M5 4l.5 9h5L11 4"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const UploadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M8 1v9M5 4l3-3 3 3M2 12v2a1 1 0 001 1h10a1 1 0 001-1v-2"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);
const DownloadIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M8 1v9M5 7l3 3 3-3M2 12v2a1 1 0 001 1h10a1 1 0 001-1v-2"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const FileIcon = () => (
  <svg width="32" height="32" viewBox="0 0 20 20" fill="none">
    <path d="M5 2h7l4 4v11a1 1 0 01-1 1H5a1 1 0 01-1-1V3a1 1 0 011-1z"
      stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    <path d="M12 2v4h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
